import { recoveryPoolInnerCall } from "./sdk";
import { ethers } from "ethers";
import { SHIELDED_POOL_JOINSPLIT_V2_INTERFACE } from "@stealthos/sdk";
import {
  listFlatJoinSplitV2SettlementJournal,
  type FlatJoinSplitV2JournalRecord,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import type { JournalBackupOptions } from "./journalBackup";
import type { OperationLog } from "./operationTypes";
import { withFlatJoinSplitV2WalletLock } from "./walletLock";
import { withReadCancellation } from "./readCancellation";
import {
  assertFlatRelayFeeRecipient,
  chooseFlatRelayRoute,
} from "./relayRouting";

const RETRY_METHODS = {
  joinsplit: "joinsplit",
  withdraw: "withdraw",
  withdraw_batch: "withdrawBatch",
  migrate: "migrate",
} as const;

export interface SavedFlatSettlementRetry {
  id: string;
  calldataHash: string;
  kind: keyof typeof RETRY_METHODS;
  token: string;
  payouts: Array<{
    recipient: string;
    private: boolean;
    keepLocal: boolean;
    amount: string;
  }>;
  fee: string;
  createdAt: number;
}

function preview(
  record: FlatJoinSplitV2JournalRecord,
  scope: FlatJoinSplitV2JournalScope,
): SavedFlatSettlementRetry | null {
  try {
    const method = RETRY_METHODS[record.kind];
    if (
      !method ||
      record.state !== "prepared" ||
      record.swapOutputSeed ||
      record.txHash ||
      BigInt(record.chainId) !== BigInt(scope.chainId) ||
      BigInt(record.transaction.chainId) !== BigInt(scope.chainId) ||
      ethers.getAddress(record.poolAddress) !==
        ethers.getAddress(scope.poolAddress) ||
      ethers.getAddress(record.accountAddress) !==
        ethers.getAddress(scope.accountAddress) ||
      ethers.getAddress(record.transaction.to) !==
        ethers.getAddress(scope.poolAddress) ||
      ethers.getAddress(record.transaction.from) !==
        ethers.getAddress(scope.accountAddress) ||
      BigInt(record.transaction.value) !== 0n ||
      record.calldataHash !== ethers.keccak256(record.transaction.data)
    )
      return null;
    const innerCall = recoveryPoolInnerCall(record.transaction.data);
    const decoded = SHIELDED_POOL_JOINSPLIT_V2_INTERFACE.parseTransaction({
      data: innerCall,
    });
    if (
      decoded?.name !== method ||
      SHIELDED_POOL_JOINSPLIT_V2_INTERFACE.encodeFunctionData(
        decoded.fragment,
        decoded.args,
      ).toLowerCase() !== innerCall.toLowerCase()
    )
      return null;
    const data = decoded.args[0];
    const nullifiers = Array.from(data.nullifiers, (value) =>
      String(value).toLowerCase(),
    );
    const commitments = data.commitments
      ? Array.from(data.commitments, (value) => String(value).toLowerCase())
      : data.changeCommitment !== ethers.ZeroHash
        ? [String(data.changeCommitment).toLowerCase()]
        : [];
    const same = (a: readonly string[], b: readonly string[]) =>
      a.length === b.length &&
      a.every((value, index) => value.toLowerCase() === b[index].toLowerCase());
    const token = ethers.getAddress(data.token);
    if (
      nullifiers.length === 0 ||
      !same(nullifiers, record.sourceNullifiers) ||
      !same(
        commitments,
        record.outputNotes.map((note) => note.commitment),
      ) ||
      record.deliveries.length !== record.outputNotes.length ||
      record.outputNotes.some(
        (note) =>
          note.leafIndex !== -1 ||
          note.value <= 0n ||
          ethers.getAddress(note.tokenData.tokenAddress) !== token,
      ) ||
      record.deliveries.some(
        (delivery) =>
          delivery.disposition ||
          delivery.completedAt ||
          !commitments.includes(delivery.commitment.toLowerCase()),
      )
    )
      return null;
    const payouts: SavedFlatSettlementRetry["payouts"] = [];
    if (record.kind === "withdraw") {
      if (data.amount <= 0n || data.fee > data.amount) return null;
      payouts.push({
        recipient: ethers.getAddress(data.recipient),
        private: false,
        keepLocal: false,
        amount: (data.amount - data.fee).toString(),
      });
    } else if (record.kind === "withdraw_batch") {
      if (data.recipients.length !== data.amounts.length) return null;
      for (let index = 0; index < data.recipients.length; index++) {
        payouts.push({
          recipient: ethers.getAddress(data.recipients[index]),
          private: false,
          keepLocal: false,
          amount: data.amounts[index].toString(),
        });
      }
    }
    for (const note of record.outputNotes) {
      const delivery = record.deliveries.find(
        (entry) =>
          entry.commitment.toLowerCase() === note.commitment.toLowerCase(),
      );
      if (!delivery) return null;
      payouts.push({
        recipient: delivery.recipientMpkHex,
        private: true,
        keepLocal: delivery.keepLocal,
        amount: note.value.toString(),
      });
    }
    return {
      id: record.id,
      calldataHash: record.calldataHash,
      kind: record.kind as SavedFlatSettlementRetry["kind"],
      token,
      payouts,
      fee: data.fee?.toString() ?? "0",
      createdAt: record.createdAt,
    };
  } catch {
    return null;
  }
}

/** Decrypt local records for review. Listing never sends or reproves anything. */
export async function listSavedFlatSettlementRetries(
  scope: FlatJoinSplitV2JournalScope,
): Promise<SavedFlatSettlementRetry[]> {
  return (await listFlatJoinSplitV2SettlementJournal(scope)).flatMap(
    (record) => {
      const reviewed = preview(record, scope);
      return reviewed ? [reviewed] : [];
    },
  );
}

/** Explicit user retry of one unchanged intent. Nullifiers make replay idempotent. */
export async function retrySavedFlatSettlement(params: {
  scope: FlatJoinSplitV2JournalScope;
  recordId: string;
  calldataHash: string;
  signer: ethers.Signer;
  provider: ethers.Provider;
  isCurrent: () => boolean;
  signal?: AbortSignal;
  backupOptions?: JournalBackupOptions;
  onLog: OperationLog;
}) {
  const assertCurrent = () => {
    if (!params.isCurrent() || params.signal?.aborted)
      throw new DOMException("Saved transaction retry cancelled", "AbortError");
  };
  assertCurrent();
  if (typeof globalThis.navigator?.locks?.request !== "function")
    throw new Error(
      "Safe saved-transaction retry needs a browser with Web Locks support",
    );
  return withFlatJoinSplitV2WalletLock({
    chainId: params.scope.chainId,
    poolAddress: params.scope.poolAddress,
    accountAddress: params.scope.accountAddress,
    signal: params.signal,
    run: async () => {
      assertCurrent();
      const [
        { reconcileFlatJoinSplitV2SettlementJournal },
        { persistFlatJournalBackup },
        { sendSimulated },
      ] = await Promise.all([
        import("./journalReconciliation"),
        import("./journalBackup"),
        import("./settlement"),
      ]);
      assertCurrent();
      const reconciled = await reconcileFlatJoinSplitV2SettlementJournal({
        provider: params.provider,
        scope: params.scope,
      });
      assertCurrent();
      if (reconciled.issues.has(params.recordId))
        throw new Error(
          "Saved transaction needs a successful chain check before retrying",
        );
      const readReviewed = async () => {
        const record = (
          await listFlatJoinSplitV2SettlementJournal(params.scope)
        ).find((entry) => entry.id === params.recordId);
        assertCurrent();
        if (
          !record ||
          !preview(record, params.scope) ||
          record.calldataHash !== params.calldataHash
        )
          throw new Error(
            "Saved transaction changed or already settled. Refresh recovery before continuing.",
          );
        return record;
      };
      const record = await readReviewed();
      const reviewed = preview(record, params.scope)!;
      const pool = new ethers.Contract(
        params.scope.poolAddress,
        [
          "function depositMergeFeeMode() view returns(uint256)",
          "function feeRecipient() view returns(address)",
        ],
        params.provider,
      );
      const mode = await withReadCancellation(
        () => pool.depositMergeFeeMode(),
        params.signal,
      );
      assertCurrent();
      if (mode !== 0n)
        throw new Error(
          "Saved transaction retry requires a baseline pool without private fees",
        );
      const route = await withReadCancellation(
        () =>
          chooseFlatRelayRoute({
            chainId: BigInt(params.scope.chainId),
            pool: params.scope.poolAddress,
            fee: BigInt(reviewed.fee),
            signal: params.signal,
            onLog: params.onLog,
            confirmFallback: () => false,
          }),
        params.signal,
      );
      assertCurrent();
      if (!route.relay)
        throw new Error("Saved transaction retry requires the relayer");
      if (BigInt(reviewed.fee) > 0n) {
        const feeRecipient = await withReadCancellation(
          () => pool.feeRecipient(),
          params.signal,
        );
        assertFlatRelayFeeRecipient(route, feeRecipient, BigInt(reviewed.fee));
      }
      assertCurrent();
      const transaction = {
        to: record.transaction.to,
        data: record.transaction.data as `0x${string}`,
        value: 0n,
        chainId: BigInt(record.transaction.chainId),
      };
      return sendSimulated(
        params.signer,
        params.provider,
        transaction,
        params.scope.poolAddress,
        record.outputNotes,
        params.onLog,
        params.scope.accountAddress,
        async (signed) => {
          await readReviewed();
          if (
            signed.to !== transaction.to ||
            signed.data !== transaction.data ||
            signed.value !== 0n ||
            signed.chainId !== transaction.chainId
          )
            throw new Error("Saved transaction changed before retry");
          await persistFlatJournalBackup(params.scope, record.id, {
            ...params.backupOptions,
            signal: params.signal,
            resume: true,
          });
          assertCurrent();
          return { id: record.id, scope: params.scope };
        },
        true,
        params.signal,
        undefined,
        params.provider,
      );
    },
  });
}
